import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { BotIcon, UserIcon, FileTextIcon, ExternalLinkIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { ChatMessage as ChatMessageType } from '../lib/chatContext';
import { AdaptiveContentRenderer } from './chat/AdaptiveContentRenderer';
import { InsightBlock } from './chat/InsightBlock';
import { ActionSuggestions } from './chat/ActionSuggestions';
import { analyzeMessageForInsights } from '../lib/insightExtractor';
import { detectActionSuggestions, shouldShowActionSuggestions, ActionSuggestion } from '../lib/actionSuggestionDetector';
interface ChatMessageProps {
  message: ChatMessageType;
  isUser?: boolean;
  onNavigate?: (view: string, metadata?: any) => void;
  onAction?: (action: string, metadata?: any) => void;
  accountContext?: {
    accountId?: string;
    accountName?: string;
  };
}
const MAX_HEIGHT = 400;
export function ChatMessage({
  message,
  isUser,
  onNavigate,
  onAction,
  accountContext
}: ChatMessageProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [insights, setInsights] = useState<string[]>([]);
  const [actionSuggestions, setActionSuggestions] = useState<ActionSuggestion[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const fullContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (contentRef.current && contentRef.current.scrollHeight > MAX_HEIGHT) {
      setNeedsExpansion(true);
    }
  }, [message.content]);
  // Analyze message for insights (only for assistant messages)
  useEffect(() => {
    if (message.role === 'assistant' && !isUser) {
      const analysis = analyzeMessageForInsights(message.content);
      setShowInsights(analysis.shouldShowInsights);
      setInsights(analysis.insights);
      // Detect action suggestions
      if (shouldShowActionSuggestions(message.content)) {
        const suggestions = detectActionSuggestions(message.content, accountContext);
        setActionSuggestions(suggestions);
      }
    }
  }, [message.content, message.role, isUser, accountContext]);
  const handleExpandToFullResponse = () => {
    setIsExpanded(true);
    // Smooth scroll to full content
    setTimeout(() => {
      fullContentRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  const handleActionSuggestion = (suggestion: ActionSuggestion) => {
    if (suggestion.type === 'navigation') {
      const view = suggestion.action.replace('navigate:', '');
      onNavigate?.(view, suggestion.metadata);
    } else if (suggestion.type === 'action') {
      onAction?.(suggestion.action, suggestion.metadata);
    } else if (suggestion.type === 'link' && suggestion.metadata?.url) {
      window.open(suggestion.metadata.url, '_blank');
    }
  };
  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsExpanded(!isExpanded);
  };
  if (message.role === 'system') {
    return <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} className="bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center shadow-lg shadow-blue-500/5" data-id="element-434">
        <p className="text-sm text-blue-300 font-medium" data-id="element-435">{message.content}</p>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3
  }} className={`flex gap-3.5 ${isUser ? 'justify-end' : 'justify-start'}`} data-id="element-436">
      {!isUser && <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-600/25 ring-2 ring-blue-500/20" data-id="element-437">
          <BotIcon className="w-5 h-5 text-white" data-id="element-438" />
        </div>}
      <div className="max-w-[80%] min-w-0 space-y-3" data-id="element-439">
        {/* Insight Block - only for assistant messages */}
        {showInsights && insights.length > 0 && <InsightBlock insights={insights} onExpandClick={handleExpandToFullResponse} data-id="element-440" />}
        {/* Main message content */}
        <div ref={fullContentRef} className={`rounded-xl p-4 shadow-lg backdrop-blur-sm transition-all ${isUser ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-blue-600/25' : 'bg-slate-800/80 text-slate-100 border border-slate-700/50 shadow-black/20'}`} data-id="element-441">
          <div ref={contentRef} className={`overflow-hidden transition-all duration-300 ${!isExpanded && needsExpansion ? 'max-h-[400px]' : ''}`} style={!isExpanded && needsExpansion ? {
          maskImage: 'linear-gradient(to bottom, black 85%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 85%, transparent 100%)'
        } : undefined} data-id="element-442">
            <AdaptiveContentRenderer content={message.content} isUser={isUser} data-id="element-443" />
          </div>
          {needsExpansion && <button onClick={handleToggleExpand} className={`mt-3 text-xs font-medium flex items-center gap-1.5 transition-colors px-2 py-1 rounded-lg ${isUser ? 'text-blue-100 hover:text-white hover:bg-blue-500/20' : 'text-blue-300 hover:text-blue-200 hover:bg-slate-700/50'}`} data-id="element-444">
              {isExpanded ? <>
                  <ChevronUpIcon className="w-3.5 h-3.5" data-id="element-445" />
                  Show less
                </> : <>
                  <ChevronDownIcon className="w-3.5 h-3.5" data-id="element-446" />
                  Show more
                </>}
            </button>}
        </div>
        {/* Resources */}
        {message.resources && message.resources.length > 0 && <motion.div initial={{
        opacity: 0,
        y: -5
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        delay: 0.1
      }} className="space-y-2" data-id="element-447">
            {message.resources.map((resource, idx) => <motion.div key={`${resource.kind}-${idx}`} initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.2,
          delay: 0.05 * idx
        }} className="bg-slate-900/60 backdrop-blur-sm border border-slate-700/50 rounded-lg p-3.5 hover:border-slate-600 hover:bg-slate-900/80 transition-all shadow-lg shadow-black/10 group" data-id="element-448">
                {resource.kind === 'doc' ? <div className="flex items-center gap-3" data-id="element-449">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20 transition-colors" data-id="element-450">
                      <FileTextIcon className="w-4 h-4 text-blue-400" data-id="element-451" />
                    </div>
                    <span className="text-sm text-slate-300 truncate font-medium" data-id="element-452">
                      {resource.title}
                    </span>
                  </div> : <a href={resource.href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-blue-400 transition-colors" data-id="element-453">
                    {resource.favicon ? <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0 overflow-hidden" data-id="element-454">
                        <img src={resource.favicon} alt="" className="w-5 h-5" data-id="element-455" />
                      </div> : <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0" data-id="element-456">
                        <ExternalLinkIcon className="w-4 h-4 text-blue-400" data-id="element-457" />
                      </div>}
                    <span className="text-sm text-slate-300 truncate flex-1 group-hover:text-blue-300 font-medium" data-id="element-458">
                      {resource.title}
                    </span>
                    <ExternalLinkIcon className="w-3.5 h-3.5 text-slate-500 flex-shrink-0 group-hover:text-blue-400 transition-colors" data-id="element-459" />
                  </a>}
              </motion.div>)}
          </motion.div>}
        {/* Action Suggestions - only for assistant messages */}
        {!isUser && actionSuggestions.length > 0 && <ActionSuggestions suggestions={actionSuggestions} onAction={handleActionSuggestion} data-id="element-460" />}
      </div>
      {isUser && <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center flex-shrink-0 shadow-lg shadow-black/20 ring-2 ring-slate-600/20" data-id="element-461">
          <UserIcon className="w-5 h-5 text-white" data-id="element-462" />
        </div>}
    </motion.div>;
}